import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, 
  KeyRound, 
  CheckCircle, 
  Loader2, 
  AlertCircle, 
  Eye, 
  EyeOff,
  ShieldCheck,
  XCircle,
  Mail,
  Lock,
  Shield
} from 'lucide-react';
import Logo from '../components/Logo';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = searchParams.get('token');
  
  // Determine initial mode based on URL path and token
  const isForgotPasswordPath = location.pathname === '/forgot-password';
  const initialStep = token ? 'reset' : (isForgotPasswordPath ? 'request' : 'request');
  
  const [step, setStep] = useState(initialStep);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenEmail, setTokenEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [] });

  // Verify token on mount if present
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  // Check password strength
  useEffect(() => {
    if (password) {
      checkPasswordStrength(password);
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [password]);

  const verifyToken = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setTokenEmail(response.data.email || '');
        setUserName(response.data.user_name || '');
        setStep('reset');
      }
    } catch (error) {
      setTokenValid(false);
      toast.error(error.response?.data?.detail || 'Invalid or expired reset link');
    } finally {
      setLoading(false);
    }
  };

  const checkPasswordStrength = (pwd) => {
    let score = 0;
    const feedback = [];
    
    if (pwd.length >= 8) score += 1;
    else feedback.push('At least 8 characters');
    
    if (pwd.length >= 12) score += 1;
    
    if (/[a-z]/.test(pwd)) score += 0.5;
    else feedback.push('Add lowercase letters');
    
    if (/[A-Z]/.test(pwd)) score += 0.5;
    else feedback.push('Add uppercase letters');
    
    if (/[0-9]/.test(pwd)) score += 0.5;
    else feedback.push('Add numbers');
    
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 0.5;
    else feedback.push('Add special characters');
    
    setPasswordStrength({ 
      score: Math.min(Math.round(score), 4), 
      feedback, 
      is_valid: score >= 3 && pwd.length >= 8 
    });
  };

  const getStrengthColor = (score) => {
    const colors = {
      0: 'bg-gray-200',
      1: 'bg-red-500',
      2: 'bg-orange-500',
      3: 'bg-yellow-500',
      4: 'bg-green-500'
    };
    return colors[score] || colors[0];
  };

  const getStrengthLabel = (score) => {
    const labels = {
      0: '',
      1: 'Weak',
      2: 'Fair',
      3: 'Good',
      4: 'Strong'
    };
    return labels[score] || '';
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim() });
      setStep('sent');
      toast.success('Check your email for reset instructions');
    } catch (error) {
      // Still show success to prevent email enumeration
      setStep('sent');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/reset-password-with-token`, {
        token,
        new_password: password
      });
      
      if (response.data.success) {
        setStep('success');
        toast.success('Password reset successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Logo size="lg" className="mx-auto" />
        </div>

        {/* Request Reset Form */}
        {step === 'request' && (
          <Card className="shadow-xl border-0" data-testid="forgot-password-form">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl">Forgot Password?</CardTitle>
              <CardDescription>
                No worries! Enter your email and we'll send you reset instructions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                  disabled={loading}
                  data-testid="send-reset-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Reset Link
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="justify-center">
              <Link 
                to="/login" 
                className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </Link>
            </CardFooter>
          </Card>
        )}

        {/* Email Sent Confirmation */}
        {step === 'sent' && (
          <Card className="shadow-xl border-0" data-testid="email-sent-confirmation">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl">Check Your Email</CardTitle>
              <CardDescription>
                We've sent password reset instructions to:
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="font-semibold text-lg text-gray-900 mb-4">{email}</p>
              
              <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800 mb-4">
                <p>Click the link in the email to reset your password. The link will expire in 1 hour.</p>
              </div>

              <div className="text-sm text-gray-500 space-y-2 text-left">
                <p className="font-medium">Didn't receive the email?</p>
                <ul className="list-disc list-inside ml-2">
                  <li>Check your spam folder</li>
                  <li>Make sure you entered the correct email</li>
                  <li>Wait a few minutes and try again</li>
                </ul>
              </div>

              <Button 
                variant="outline" 
                className="mt-6"
                onClick={() => { setStep('request'); setEmail(''); }}
              >
                Try a different email
              </Button>
            </CardContent>
            <CardFooter className="justify-center">
              <Link 
                to="/login" 
                className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </Link>
            </CardFooter>
          </Card>
        )}

        {/* Reset Password Form (with token) */}
        {step === 'reset' && (
          <Card className="shadow-xl border-0" data-testid="reset-password-form">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl">Reset Your Password</CardTitle>
              <CardDescription>
                {tokenValid === null ? (
                  'Verifying your reset link...'
                ) : tokenValid ? (
                  <>Create a new password for <span className="font-semibold">{tokenEmail}</span></>
                ) : (
                  'Invalid or expired reset link'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading && tokenValid === null ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                </div>
              ) : tokenValid === false ? (
                <div className="text-center py-4">
                  <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    This password reset link is invalid or has expired.
                  </p>
                  <Button 
                    onClick={() => { setStep('request'); navigate('/forgot-password'); }}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Request New Reset Link
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {userName && (
                    <p className="text-center text-gray-600 mb-2">
                      Welcome back, <span className="font-semibold">{userName}</span>!
                    </p>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        minLength={8}
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    
                    {/* Password Strength Indicator */}
                    {password && (
                      <div className="space-y-1">
                        <div className="flex gap-1">
                          {[1, 2, 3, 4].map((level) => (
                            <div 
                              key={level}
                              className={`h-1.5 flex-1 rounded ${
                                level <= passwordStrength.score ? getStrengthColor(passwordStrength.score) : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between items-center">
                          <span className={`text-xs ${
                            passwordStrength.score <= 1 ? 'text-red-600' :
                            passwordStrength.score <= 2 ? 'text-orange-600' :
                            passwordStrength.score <= 3 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {getStrengthLabel(passwordStrength.score)}
                          </span>
                          {passwordStrength.feedback.length > 0 && (
                            <span className="text-xs text-gray-500">
                              {passwordStrength.feedback[0]}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <XCircle className="h-3 w-3" />
                        Passwords do not match
                      </p>
                    )}
                    {confirmPassword && password === confirmPassword && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Passwords match
                      </p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                    disabled={loading || password.length < 8 || password !== confirmPassword}
                    data-testid="reset-password-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="h-4 w-4 mr-2" />
                        Reset Password
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* Success */}
        {step === 'success' && (
          <Card className="shadow-xl border-0" data-testid="reset-success">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl text-green-700">Password Reset!</CardTitle>
              <CardDescription>
                Your password has been successfully reset.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">
                You can now log in with your new password.
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
